import React from "react";
import { Loader2, Pencil, Trash2, Warehouse } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatUnits, getEquipmentIcon, getOccupancy } from "./storageLocationUtils";

/** Um local: identificação, equipamento, status, nível de ocupação e ações */
function StorageLocationCard({ location, editing, deleting, onEdit, onDelete }) {
  const { capacity, available, usage, status } = getOccupancy(location);
  const EquipmentIcon = getEquipmentIcon(location.equipment_name);
  const name = location.name || "Sem nome";
  const excess = available < 0;

  return (
    <article
      className={cn(
        "relative flex flex-col gap-3.5 rounded-2xl border bg-card p-4 shadow-soft transition-all",
        editing ? "border-2 border-primary/60 shadow-md" : "border-border hover:border-brand-pistachio/60",
        deleting && "opacity-60"
      )}
    >
      {editing && (
        <span className="absolute -top-2.5 right-4 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground shadow-sm">
          Em edição
        </span>
      )}

      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-3">
          <div
            className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-surface-container text-primary"
            aria-hidden="true"
          >
            <Warehouse className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex min-w-0 items-baseline gap-1.5">
              <span className="flex-shrink-0 font-mono text-[11px] text-muted-foreground">#{location.id}</span>
              <h3 className="truncate font-display text-base font-semibold leading-tight text-foreground" title={name}>
                {name}
              </h3>
            </div>
            <div className="mt-0.5 flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
              <EquipmentIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              <span className={cn("truncate", !location.equipment_name && "italic")}>
                {location.equipment_name || "Sem equipamento"}
              </span>
            </div>
          </div>
        </div>
        <span
          className={cn(
            "inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold",
            status.badge
          )}
        >
          <span className={cn("h-1.5 w-1.5 rounded-full", status.dot)} aria-hidden="true" />
          {status.label}
        </span>
      </div>

      <div className="space-y-2 rounded-xl bg-surface-low p-3">
        <div className="flex items-center justify-between text-[13px] font-semibold">
          <span className="text-muted-foreground">Nível de ocupação</span>
          <span className={cn("font-display text-base font-bold", status.text)}>
            {usage === null ? "—" : `${usage}%`}
          </span>
        </div>
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-surface-high"
          role="progressbar"
          aria-label={`Ocupação de ${name}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.min(usage ?? 0, 100)}
        >
          <div
            className={cn("h-full rounded-full transition-all duration-500", status.bar)}
            style={{ width: `${Math.min(usage ?? 0, 100)}%` }}
          />
        </div>
        <div className="grid grid-cols-2 pt-1">
          <div className="flex flex-col">
            <span className="text-[11px] font-semibold text-muted-foreground">Capacidade Total</span>
            <span className="text-[13px] font-semibold text-foreground">
              {capacity > 0 ? `${formatUnits(capacity)} un` : "Não informada"}
            </span>
          </div>
          <div className="flex flex-col items-end text-right">
            <span className="text-[11px] font-semibold text-muted-foreground">Saldo Disponível</span>
            <span className={cn("text-[13px] font-semibold", excess ? "text-destructive" : "text-foreground")}>
              {formatUnits(available)} un{excess && " (Excesso)"}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => onEdit(location)}
          disabled={deleting}
          className={cn(
            "flex h-8 items-center gap-1.5 rounded-full border px-3.5 text-xs transition-colors disabled:opacity-60",
            editing
              ? "border-brand-pistachio/60 bg-secondary font-semibold text-primary"
              : "border-border bg-surface-container font-medium text-foreground hover:bg-surface-high"
          )}
        >
          <Pencil className="h-3.5 w-3.5" />
          {editing ? "Editando..." : "Editar"}
        </button>
        <button
          type="button"
          onClick={() => onDelete(location)}
          disabled={deleting}
          aria-label={`Excluir ${name}`}
          className="flex h-8 items-center gap-1.5 rounded-full border border-destructive/25 bg-destructive/5 px-3.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-60"
        >
          {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          Excluir
        </button>
      </div>
    </article>
  );
}

export default React.memo(StorageLocationCard);
